/**
 * SeasonalityModeView Types
 *
 * Shared types and interfaces used by SeasonalityModeView and its helper modules.
 */

import { MonthlyTotals } from '../../types';
import { UnitCountsData } from '../../services/api';
import { AccountDefinition } from '../../config/docTemplate';
import { ProjectionInputRecord, ProjectionCalculatedRecord } from '../../types/projections';
import { ProjectionViewData } from '../../types/projectionViewData';

/**
 * Historical data bundle for a single year
 */
export interface HistoricalYearBundle {
  year: number;
  accounts: {
    account: string;
    description: string;
    ytdTotal: number;
    monthlyTotals: MonthlyTotals;
  }[];
  docGross: {
    department: string;
    displayName: string;
    sales?: MonthlyTotals;
    cos?: MonthlyTotals;
    gross?: MonthlyTotals;
    ytdSales?: number;
    ytdCos?: number;
    ytdGross: number;
    monthlyGross?: MonthlyTotals;
    // Combined total gross for vehicle departments (front-end + F&I)
    totalGross?: MonthlyTotals;
    ytdTotalGross?: number;
    // Operating Profit fields (Gross - Expenses) for seasonality
    totalExpenses?: MonthlyTotals;
    operatingProfit?: MonthlyTotals;
    ytdTotalExpenses?: number;
    ytdOperatingProfit?: number;
    // Service department pay-type gross breakdown (Customer/Warranty/Internal/Other)
    serviceGrossByPayType?: {
      customer: { gross: MonthlyTotals; ytdGross: number };
      warranty: { gross: MonthlyTotals; ytdGross: number };
      internal: { gross: MonthlyTotals; ytdGross: number };
      other: { gross: MonthlyTotals; ytdGross: number };
    };
    // Parts department pay-type gross breakdown (7 categories)
    partsGrossByPayType?: {
      customerParts: { gross: MonthlyTotals; ytdGross: number };
      warrantyParts: { gross: MonthlyTotals; ytdGross: number };
      internalParts: { gross: MonthlyTotals; ytdGross: number };
      counterRetail: { gross: MonthlyTotals; ytdGross: number };
      counterWholesale: { gross: MonthlyTotals; ytdGross: number };
      otherParts: { gross: MonthlyTotals; ytdGross: number };
      inventoryAdjustment: { gross: MonthlyTotals; ytdGross: number };
    };
  }[];
  unitCountsData: UnitCountsData | null;
  // Location-level Operating Profit for seasonality calculation
  locationOperatingProfit?: {
    ytdTotal: number;
    monthlyTotals: MonthlyTotals;
  };
}

/**
 * Calculated aggregate values for each department (to be saved to backend)
 */
export interface DepartmentAggregates {
  department: string;
  // Gross (for all departments - needed for Forecast page projection line)
  gross: number;
  grossMonthly: MonthlyTotals;
  // Units (only for vehicle departments)
  units?: number;
  unitsMonthly?: MonthlyTotals;
  // Expenses and profit
  totalExpenses: number;
  totalExpensesMonthly: MonthlyTotals;
  operatingProfit: number;
  operatingProfitMonthly: MonthlyTotals;
  // Individual expense account dollar amounts (for consolidated aggregation)
  // Key: account code (e.g., "011A"), Value: { annual: dollar amount, monthly: monthly dollar amounts }
  expenseAccounts?: Record<string, { annual: number; monthly: MonthlyTotals }>;
  // Individual income account dollar amounts (for OTHERS department consolidated aggregation)
  // Key: account code (e.g., "850"), Value: { annual: total dollar amount, monthly: monthly dollar amounts }
  incomeAccounts?: Record<string, { annual: number; monthly: MonthlyTotals }>;
}

/**
 * Pre-calculated expense account amounts for consolidated view
 * Key format: "dept#EXPENSE_accountCode" e.g., "NEW_VEHICLE#EXPENSE_011A"
 */
export interface ExpensesByAccount {
  [key: string]: {
    annualTotal: number;
    monthlyDistribution: Record<string, number>;
  };
}

/**
 * Row color classes (matching DOCReportView)
 */
export const ROW_COLORS = {
  sales: 'bg-blue-50 text-blue-900',
  gross: 'bg-green-50 text-green-900',
  fiIncome: 'bg-green-50 text-green-900',
  totalGross: 'bg-green-100 text-green-900',
  variableExpense: 'bg-orange-50 text-orange-900',
  semiFixedExpense: 'bg-amber-50 text-amber-900',
  salesExpense: 'bg-yellow-50 text-yellow-900',
  fixedExpense: 'bg-purple-50 text-purple-900',
  otherExpense: 'bg-gray-100 text-gray-900',
  totalExpense: 'bg-red-50 text-red-900',
  profit: 'bg-emerald-100 text-emerald-900',
  loss: 'bg-red-100 text-red-900',
  rentalIncome: 'bg-teal-50 text-teal-900',
};

/**
 * Map category names to colors
 */
export const CATEGORY_COLORS: Record<string, string> = {
  'VARIABLE': ROW_COLORS.variableExpense,
  'SEMI_FIXED': ROW_COLORS.semiFixedExpense,
  'SALES': ROW_COLORS.salesExpense,
  'FIXED': ROW_COLORS.fixedExpense,
  'OTHERS': ROW_COLORS.otherExpense,
  'RENTAL_INCOME': ROW_COLORS.rentalIncome,
  'RENTAL_SELLING': ROW_COLORS.variableExpense,
  'RENTAL_FIXED': ROW_COLORS.fixedExpense,
};

/**
 * Executive monthly adjustments: who may adjust, which projection, and what to do after a save
 */
export interface MonthlyAdjustmentContext {
  canAdjust: boolean;
  locationId: number;
  targetYear: number;
  onSaved: () => Promise<void> | void;
}

/**
 * Props for SeasonalityModeView (the Monthly Seasonality view)
 */
export interface SeasonalityModeViewProps {
  inputs: ProjectionInputRecord[];
  // Stored seasonality calculations from DynamoDB - used to ensure consistency with Forecast page
  calculations?: ProjectionCalculatedRecord[];
  localInputs: Record<string, { value: number; inputType: 'amount' | 'percentage'; isDirty: boolean }>;
  // Display years, ascending (4 during the Oct-Dec window: incl. the partial prior year)
  historicalYears: number[];
  // Years driving the 50/30/20 weights, NEWEST FIRST (getSeasonalityYears)
  seasonalityYears: number[];
  // True before Jan 10 of the target year - show the provisional-spread banner
  seasonalityProvisional?: boolean;
  historicalDataByYear: Record<number, HistoricalYearBundle | null>;
  targetYear: number;
  // Location name for export filename
  locationName: string;
  formatCurrency: (value: number) => string;
  getProjectionValue: (department: string, metric: string) => number | null;
  getTotalProjectedUnits: (department: string) => number;
  getCalculatedGross: (department: string, metric: 'GROSS' | 'FI_INCOME') => { total: number; perUnit: number };
  getCategoryAccountExpenses: (department: string, accounts: AccountDefinition[], deptGross: number) => { totalAmount: number; totalPercent: number };
  getServiceDeptProjections: (department: string) => { gross: number; grossToSalesPercent: number; calculatedSales: number };
  // Callback to pass calculated aggregates back to parent for saving
  onAggregatesCalculated?: (aggregates: DepartmentAggregates[]) => void;
  // Comparison mode: show actual data alongside projected
  comparisonMode?: boolean;
  // Actual data for the target year (when comparison mode is enabled)
  actualData?: HistoricalYearBundle | null;
  // Consolidated view (aggregated data, simplified unit display)
  isConsolidated?: boolean;
  // Pre-calculated expense account amounts for consolidated view (exact sums from all locations)
  expensesByAccount?: ExpensesByAccount;
  // Pre-computed view data from backend (for simplified rendering)
  // When available, used instead of local calculations
  viewData?: ProjectionViewData;
  // Department assignment filtering for DepartmentManagers
  assignedDepartmentIds?: string[];
  // Executive monthly adjustments (pencil + badge on adjustable rows)
  monthlyAdjustment?: MonthlyAdjustmentContext;
}
